import {
  ArrowRight,
  FileText,
  Link2,
  LockKeyhole,
  QrCode,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { navigate } from "../app/router";

export function HomePage() {
  return (
    <>
      <section className="hero">
        <div>
          <p className="eyebrow">Local-first workspace</p>
          <h2>Create the document. Choose how it travels.</h2>
          <p>
            Build a professional quotation, save it locally, then send the exact file by
            visual QR or a browser-encrypted three-day link.
          </p>
        </div>
        <div className="privacy-pill">
          <LockKeyhole aria-hidden="true" size={18} />
          <span>No accounts. No cloud drafts.</span>
        </div>
      </section>

      <section className="module-grid" aria-label="Start">
        <article className="module-card quote-card">
          <div className="module-icon">
            <FileText aria-hidden="true" />
          </div>
          <p className="eyebrow">Quotation maker</p>
          <h2>Build with confidence</h2>
          <p>
            Edit line items, separate recurring and one-time charges, preview a light
            customer document, and export PDF or DOCX locally.
          </p>
          <button
            className="button button-primary"
            type="button"
            onClick={() => navigate("/quotations")}
          >
            New quotation <ArrowRight aria-hidden="true" size={18} />
          </button>
        </article>

        <article className="module-card transfer-card">
          <div className="module-icon">
            <QrCode aria-hidden="true" />
          </div>
          <p className="eyebrow">File transfer</p>
          <h2>Local QR or encrypted link</h2>
          <p>
            Use QR for nearby, no-upload transfer. Choose Send via link only when
            internet delivery is more practical.
          </p>
          <div className="button-row">
            <button
              className="button button-primary"
              type="button"
              onClick={() => navigate("/transfer?mode=qr")}
            >
              Send by QR
            </button>
            <button
              className="button button-secondary"
              type="button"
              onClick={() => navigate("/link")}
            >
              <Link2 aria-hidden="true" size={18} /> Send via link
            </button>
          </div>
        </article>
      </section>

      <section className="trust-grid">
        <article>
          <Sparkles aria-hidden="true" />
          <h3>One canonical quotation</h3>
          <p>Preview, PDF, and DOCX use the same calculation snapshot.</p>
        </article>
        <article>
          <QrCode aria-hidden="true" />
          <h3>QR stays device-to-device</h3>
          <p>No transferred bytes enter HTTP during the QR path.</p>
        </article>
        <article>
          <ShieldCheck aria-hidden="true" />
          <h3>Links encrypt first</h3>
          <p>The decryption key stays in the link fragment, outside cloud storage.</p>
        </article>
      </section>
    </>
  );
}

export function PrivacyPage() {
  return (
    <div className="reading-grid">
      <section className="info-card">
        <h2>What stays local</h2>
        <p>
          Quotation drafts, pricing inputs, generated document work, QR frames, and
          receiving chunks stay in this browser. A link decryption key is shared in the
          link fragment but is never sent to the Function, KV, or D1.
        </p>
      </section>
      <section className="info-card">
        <h2>What link mode uploads</h2>
        <p>
          Only the prepared artifact after browser-side encryption. Link mode is never
          automatic, and requires a fresh confirmation for each upload.
        </p>
      </section>
      <section className="info-card">
        <h2>Bearer-link warning</h2>
        <p>
          Anyone with the complete link can download and decrypt until expiry or
          revocation. Revocation cannot retract a copy already saved.
        </p>
      </section>
      <section className="info-card">
        <h2>Visible QR is not encrypted</h2>
        <p>
          Someone who records a clear QR stream may reconstruct it. Use QR only in an
          environment you trust.
        </p>
      </section>
    </div>
  );
}

export function CompatibilityPage() {
  return (
    <div className="reading-grid">
      <section className="info-card">
        <h2>Recommended browsers</h2>
        <p>
          Recent Safari on iPhone and iPad, recent Chrome on Android, and a current
          Chromium desktop browser.
        </p>
      </section>
      <section className="info-card">
        <h2>Camera requirements</h2>
        <p>
          QR receiving needs HTTPS or localhost, camera permission, a visible rear
          camera, and enough browser storage for accepted chunks.
        </p>
      </section>
      <section className="info-card">
        <h2>File sharing</h2>
        <p>
          Operating-system Share support varies. A normal local download remains
          available when native sharing is unavailable.
        </p>
      </section>
      <section className="info-card">
        <h2>Honest MVP limits</h2>
        <p>
          QR transfer is intended for small files. Link limits are product safety
          policies and are shown before preparation.
        </p>
      </section>
    </div>
  );
}
